import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Ban, Loader2 } from 'lucide-react';
import { api, formatDate, formatDateTime, mensajeError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { puedeMarcarProhibido } from '../../lib/roles';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import IndicadorProhibido from '../IndicadorProhibido';

/**
 * PACIENTE PROHIBIDO EN LA FICHA (administración, 14/10/2026)
 * -----------------------------------------------------------
 * Lo que va junto al nombre del paciente: el indicador rojo «PROHIBIDO» (para
 * todos) y el botón «Paciente prohibido» (solo gestión y doctores,
 * `puedeMarcarProhibido`; el servidor lo vuelve a comprobar).
 *
 *  · Sin marcar: el botón abre «Marcar como paciente prohibido» con el MOTIVO
 *    obligatorio (uso interno). Si ya tenía citas futuras, al marcarlo salta
 *    un aviso que las lista, con un enlace para abrir cada una en la agenda:
 *    no se anulan solas.
 *  · Marcado: el mismo botón enseña el motivo, quién y cuándo, el historial y
 *    «Desmarcar», que pide confirmación (queda en el historial quién, cuándo
 *    y el motivo que tenía).
 *  · «Editar motivo» (23/10/2026): cambia el motivo sin desmarcarlo. Abre el
 *    mismo campo con el motivo actual ya puesto; «Guardar motivo» no se puede
 *    pulsar vacío ni con el mismo texto. Va por `PUT …/prohibido`, y en el
 *    historial queda quién, cuándo, el de antes y el nuevo.
 *
 * Va en un componente propio para tocar la cabecera de la ficha lo mínimo.
 */

const ACCIONES = {
  marcado: 'Marcado',
  desmarcado: 'Desmarcado',
  excepcion: 'Excepción para dar cita',
  fusion: 'Fusión de fichas',
  motivo_cambiado: 'Motivo cambiado',
};

const LARGO_MAXIMO_MOTIVO = 2000;  // services/paciente_prohibido.LARGO_MAXIMO_MOTIVO

/** El campo del motivo: el mismo al marcar y al editarlo. */
function CampoMotivo({ motivo, onChange, tocado, onBlur, faltaMotivo, placeholder,
  largoMaximo = LARGO_MAXIMO_MOTIVO }) {
  return (
    <div>
      <Label htmlFor="prohibido-motivo">Motivo <span className="text-red-600">*</span></Label>
      <Textarea
        id="prohibido-motivo"
        value={motivo}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        rows={4}
        maxLength={largoMaximo}
        placeholder={placeholder}
        className={`mt-1.5 ${tocado && faltaMotivo ? 'border-red-300' : ''}`}
        aria-invalid={tocado && faltaMotivo}
        data-testid="prohibido-motivo"
      />
      <div className={`text-xs mt-1 ${tocado && faltaMotivo ? 'text-red-600' : 'text-muted-foreground'}`}
        data-testid="prohibido-motivo-ayuda">
        {tocado && faltaMotivo
          ? 'El motivo es obligatorio'
          : 'Obligatorio. De uso interno: solo lo ve el equipo, nunca el paciente.'}
      </div>
    </div>
  );
}

function enlaceCita(c) {
  const q = new URLSearchParams({ cita: c.id, fecha: c.date || '', agenda: c.agenda_id || '' });
  return `/citas?${q.toString()}`;
}

/** Lo que hay que mirar tras marcarlo: las citas que ya tenía, cuántas hay
 *  en agendas que no ve quien marca (un doctor, las de otros) y las
 *  entradas de la lista de espera que se han dado de baja. */
export function hayQueAvisar(data) {
  return (Array.isArray(data?.citas_futuras) && data.citas_futuras.length > 0)
    || Number(data?.citas_futuras_otras_agendas || 0) > 0
    || !!data?.aviso_lista_espera;
}

/** El aviso de las citas que ya tenía al marcarlo. */
export function AvisoCitasFuturas({ citas, otras = 0, avisoListaEspera = '', open, onOpenChange }) {
  const lista = Array.isArray(citas) ? citas : [];
  const total = lista.length + Number(otras || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="prohibido-citas-futuras">
        <DialogHeader>
          <DialogTitle>
            {total ? `Tiene ${total === 1 ? 'una cita pendiente' : `${total} citas pendientes`}` : 'Paciente marcado como prohibido'}
          </DialogTitle>
        </DialogHeader>
        {total > 0 && (
          <p className="text-sm text-muted-foreground">
            El paciente ya está marcado como prohibido, pero sus citas siguen dadas: no se anulan
            solas. Revísalas y anula las que procedan.
          </p>
        )}
        {Number(otras || 0) > 0 && (
          <p className="text-sm text-[#13231E]" data-testid="prohibido-citas-otras-agendas">
            {Number(otras) === 1 ? '1 cita en otras agendas' : `${otras} citas en otras agendas`}: avisa a
            administración para que la{Number(otras) === 1 ? '' : 's'} revise.
          </p>
        )}
        {avisoListaEspera && (
          <p className="text-sm text-amber-800" data-testid="prohibido-aviso-lista-espera">{avisoListaEspera}</p>
        )}
        <ul className="mt-2 space-y-1.5" data-testid="prohibido-citas-futuras-lista">
          {lista.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-sm"
              data-testid={`prohibido-cita-futura-${c.id}`}
            >
              <span className="min-w-0">
                <span className="font-semibold tabular-nums">{formatDate(c.date)} · {c.start}</span>
                <span className="text-muted-foreground"> · {c.agenda_name || 'Agenda'} · {c.concept_name || 'Cita'}</span>
              </span>
              <Link
                to={enlaceCita(c)}
                target="_blank"
                rel="noopener"
                className="shrink-0 text-xs font-medium text-[#2E6E5E] hover:underline"
                data-testid={`prohibido-cita-futura-abrir-${c.id}`}
              >
                Abrir en la agenda ▸
              </Link>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)} data-testid="prohibido-citas-futuras-cerrar">
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BotonPacienteProhibido({ paciente, onCambio }) {
  const { user } = useAuth();
  const puede = puedeMarcarProhibido(user);
  const prohibido = paciente?.prohibido === true;
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [tocado, setTocado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  // «Editar motivo»: el campo del motivo, con el que tiene ya puesto.
  const [editando, setEditando] = useState(false);
  // Lo que devolvió marcar: citas futuras, las de otras agendas y la lista de espera.
  const [futuras, setFuturas] = useState(null);
  const [futurasAbierto, setFuturasAbierto] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setMotivo('');
    setTocado(false);
    setConfirmarQuitar(false);
    setEditando(false);
    setGuardando(false);
  }, [abierto]);

  if (!paciente?.id) return null;
  const faltaMotivo = !motivo.trim();
  const motivoActual = String(paciente.prohibido_motivo || '');
  const mismoMotivo = motivo.trim() === motivoActual.trim();

  const empezarAEditar = () => {
    setMotivo(motivoActual);
    setTocado(false);
    setConfirmarQuitar(false);
    setEditando(true);
  };

  const marcar = async () => {
    if (faltaMotivo) { setTocado(true); return; }
    setGuardando(true);
    try {
      const { data } = await api.post(`/api/patients/${paciente.id}/prohibido`, { motivo: motivo.trim() });
      toast.success('Paciente marcado como prohibido');
      setAbierto(false);
      if (hayQueAvisar(data)) {
        setFuturas(data);
        setFuturasAbierto(true);
      }
      onCambio?.(data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido marcar al paciente'));
      // Otra persona lo ha marcado entretanto (revisión del 23/10/2026): se cierra
      // y la ficha se vuelve a leer, para que salga «Editar motivo».
      if (e?.response?.status === 409) {
        setAbierto(false);
        onCambio?.();
      }
    } finally {
      setGuardando(false);
    }
  };

  const guardarMotivo = async () => {
    if (faltaMotivo) { setTocado(true); return; }
    if (mismoMotivo) return;
    setGuardando(true);
    try {
      const { data } = await api.put(`/api/patients/${paciente.id}/prohibido`, { motivo: motivo.trim() });
      toast.success('Motivo actualizado');
      setAbierto(false);
      onCambio?.(data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido cambiar el motivo'));
      // Otra persona lo ha cambiado o desmarcado entretanto: se cierra y la
      // ficha se vuelve a leer, para no seguir editando sobre lo que ya no es.
      if (e?.response?.status === 409) {
        setAbierto(false);
        onCambio?.();
      }
    } finally {
      setGuardando(false);
    }
  };

  const desmarcar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.delete(`/api/patients/${paciente.id}/prohibido`);
      toast.success('Ya no está marcado como prohibido');
      setAbierto(false);
      onCambio?.(data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido quitar la marca'));
      // Otra persona ya lo ha desmarcado: se cierra y la ficha se vuelve a leer.
      if (e?.response?.status === 409) {
        setAbierto(false);
        onCambio?.();
      }
    } finally {
      setGuardando(false);
    }
  };

  const historial = Array.isArray(paciente.prohibido_historial) ? paciente.prohibido_historial : [];

  return (
    <span className="inline-flex flex-wrap items-center gap-2 align-middle font-sans text-base font-normal" data-testid="paciente-prohibido">
      <IndicadorProhibido paciente={paciente} testId="ficha-indicador-prohibido" />
      {puede && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setAbierto(true)}
          aria-pressed={prohibido}
          title={prohibido ? 'Ver o editar el motivo, o quitar la marca' : 'Marcar como paciente prohibido: no se le podrán dar citas'}
          className={`h-7 px-2 text-xs ${prohibido
            ? 'border-red-400 text-red-700 hover:bg-red-50'
            : 'text-muted-foreground hover:text-red-700 hover:border-red-300'}`}
          data-testid="boton-paciente-prohibido"
        >
          <Ban className="w-3.5 h-3.5 mr-1" /> Paciente prohibido
        </Button>
      )}

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-lg" data-testid="prohibido-dialogo">
          {!prohibido ? (
            <>
              <DialogHeader>
                <DialogTitle>Marcar como paciente prohibido</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                No se le podrán dar citas (consulta ni quirófano). En su ficha y en las listas saldrá
                en rojo «PROHIBIDO».
              </p>
              <CampoMotivo
                motivo={motivo}
                onChange={setMotivo}
                tocado={tocado}
                onBlur={() => setTocado(true)}
                faltaMotivo={faltaMotivo}
                placeholder="Por qué no se le dan más citas"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button
                  type="button"
                  onClick={marcar}
                  disabled={faltaMotivo || guardando}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="prohibido-marcar"
                >
                  {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Marcar como prohibido
                </Button>
              </DialogFooter>
            </>
          ) : editando ? (
            <>
              <DialogHeader>
                <DialogTitle>Editar el motivo</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                El paciente sigue marcado como prohibido. En el historial queda quién cambia el
                motivo, cuándo, el de antes y el nuevo.
              </p>
              <CampoMotivo
                motivo={motivo}
                onChange={setMotivo}
                tocado={tocado}
                onBlur={() => setTocado(true)}
                faltaMotivo={faltaMotivo}
                placeholder="Por qué no se le dan más citas"
                largoMaximo={Math.max(LARGO_MAXIMO_MOTIVO, motivoActual.trim().length)}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditando(false)} data-testid="prohibido-editar-cancelar">
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={guardarMotivo}
                  disabled={faltaMotivo || mismoMotivo || guardando}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
                  data-testid="prohibido-guardar-motivo"
                >
                  {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Guardar motivo
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Paciente prohibido</DialogTitle>
              </DialogHeader>
              <div className="rounded-md border border-red-200 bg-red-50/60 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Motivo (uso interno)</div>
                  <button
                    type="button"
                    onClick={empezarAEditar}
                    className="shrink-0 text-xs font-medium text-[#2E6E5E] hover:underline"
                    data-testid="prohibido-editar-motivo"
                  >
                    Editar motivo
                  </button>
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#13231E]" data-testid="prohibido-motivo-actual">
                  {paciente.prohibido_motivo || '—'}
                </p>
                {(paciente.prohibido_por_nombre || paciente.prohibido_at) && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Marcado{paciente.prohibido_por_nombre ? ` por ${paciente.prohibido_por_nombre}` : ''}
                    {paciente.prohibido_at ? ` el ${formatDateTime(paciente.prohibido_at)}` : ''}
                  </div>
                )}
              </div>
              {historial.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Historial</div>
                  <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 text-xs" data-testid="prohibido-historial">
                    {[...historial].reverse().map((h, i) => (
                      <li key={`${h.accion}-${h.at}-${i}`}>
                        <span className="font-medium">{ACCIONES[h.accion] || h.accion}</span>
                        {h.por_nombre ? ` · ${h.por_nombre}` : ''}
                        {h.at ? ` · ${formatDateTime(h.at)}` : ''}
                        {h.accion === 'excepcion' && h.cita_fecha ? ` · cita del ${formatDate(h.cita_fecha)} ${h.cita_hora || ''}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {confirmarQuitar ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="prohibido-confirmar-quitar">
                  ¿Quitar la marca? Se le podrán volver a dar citas. En el historial queda quién la quita,
                  cuándo y el motivo que tenía.
                </div>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAbierto(false)}>Cerrar</Button>
                {confirmarQuitar ? (
                  <Button
                    type="button"
                    onClick={desmarcar}
                    disabled={guardando}
                    className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
                    data-testid="prohibido-desmarcar-confirmar"
                  >
                    {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Sí, desmarcar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmarQuitar(true)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="prohibido-desmarcar"
                  >
                    Desmarcar
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AvisoCitasFuturas
        citas={futuras?.citas_futuras}
        otras={futuras?.citas_futuras_otras_agendas}
        avisoListaEspera={futuras?.aviso_lista_espera}
        open={futurasAbierto}
        onOpenChange={setFuturasAbierto}
      />
    </span>
  );
}
